import React, { useContext, useMemo } from 'react'
import _ from 'lodash'
import last from 'lodash/last'
import { useSelector, shallowEqual, useStore } from 'react-redux'
import { Map, fromJS, List } from 'immutable'
import { getValueForMode } from '../constants/toUnits'
import { useLayoutMode } from './useLayoutMode'
import { IS_SERVER } from '../constants/ssr'
import { getStripRootMeta } from '../components/WidgetBuilder/UI/ProjectStyles'
import { useLangDirection } from './useLangDirection'
import { CHAT_BOT_TYPE, LANDING_TYPE } from '../constants/features'
import { kebabCase } from '../constants/addStyleClasses'
import { DISABLE_INHERIT_LIST } from '../constants/StyleContants'
import { ALL_SIZE_KEYS_NO_UNIT } from '../constants/CardComponent'
import { isSame } from '../components/WidgetBuilder/UI/isSame'
import { StripRepeatContext } from '../components/WidgetBuilder/UI/Components/Elements/Grid/GridContext'
import { useLocation } from './useLocation'
import { getAllChildren } from '../components/WidgetBuilder/UI/Components/lib'
import { getPageUID } from '../lib/getValueByType'
import { createProjectComponentStyleSelector } from './useComponentStyle/selectors'

const isObjectID = (val) => /^[0-9a-fA-F]{24}$/.test(val)

const isStaticWidget = (id) =>
  _.get(window, '__FT_APP_STATE__.widget._id') === id || location.href.indexOf(`/widgets/${id}`) !== -1

export const getRepeatRootPath = (widgetPath) =>
  _.startsWith(_.last(widgetPath), 'i?') ? _.slice(widgetPath, 0, widgetPath.length - 2) : widgetPath

export const getRootPath = (_widgetPath) => {
  const widgetPath = getRepeatRootPath(_widgetPath)
  return widgetPath?.length === 0
    ? ['widgets', 'current']
    : (widgetPath?.length ?? 1) === 1 && isObjectID(widgetPath?.[0]) && isStaticWidget(widgetPath?.[0])
      ? ['widgets', 'current']
      : ['widgets', 'data', last(widgetPath)]
}

export const useAutoSizeParent = (uid) => {
  const { isRepeat, index } = useContext(StripRepeatContext)
  const autoSizeIndex = useSelector((state) => state.getIn(['autoSize', isRepeat ? `${uid}-${index}` : uid], 0))
  return { autoSizeIndex }
}

export const useIsEmailSurveyProject = () =>
  useSelector(
    (state) =>
      state.getIn(['project', 'ProjectType'], 'web') === 'surveyEmail' &&
      !(
        Boolean(state.get('page')) &&
        state.hasIn(['pages', state.get('page'), 'Props']) &&
        Boolean(state.getIn(['pages', state.get('page'), 'Props', 'Grid', 0]))
      )
  )

export const useProjectComponent = (uid, PageID) => {
  const component = useSelector((state) =>
    state.getIn(
      PageID === 'project' ? ['project', 'Settings', 'elements', uid] : ['pages', PageID, 'Props', 'elements', uid],
      Map()
    )
  )
  return { component }
}

export const useChildrenRules = (uid, PageID, PageUID, isRepeat, parent, repeatUID, options, tabValue, index) => {
  let tab = tabValue
  if (!tab) {
    tab = options[0]
  }
  const elements = useSelector((state) => {
    if (PageUID === 'project') {
      return state.getIn(['project', 'Settings', 'elements'], Map())
    } else {
      return state.getIn(['pages', PageID, 'Props', 'elements'], Map())
    }
  })

  const children = getAllChildren(tab?.children || [], elements)
  let childrenRules = useSelector((state) => {
    let result = Map()
    _.each(children, (childID) => {
      let rules = state.getIn(
        [
          'rules',
          PageUID === 'project' ? parent : PageUID,
          ...(isRepeat ? [repeatUID] : []),
          childID,
          ...(isRepeat ? [index] : []),
        ],
        List()
      )
      if (List.isList(rules)) {
        if (rules.includes('hide')) {
          result = result.set(childID, 'hide')
        } else if (rules.includes('show')) {
          result = result.set(childID, 'show')
        }
      }
    })
    return result
  })

  return { childrenRules }
}

export const useRelevantParamsChange = (uid, PageID, PageUID = '') => {
  const params = useSelector((state) => state.getIn(['params']))
  const paramsJS = params ? params.toJS() : {}

  const paramsKeys = useMemo(() => {
    return _.filter(_.keys(paramsJS), (key) => key.indexOf(`${PageUID}:${uid}`) !== -1)
  }, [paramsJS])

  const values = _.pick(paramsJS, paramsKeys)
  const changes = useMemo(() => {
    return values
  }, [JSON.stringify(values)])

  return changes
}

export const useProjectComponentStyle = (uid, PageID) => {
  const style = useSelector(createProjectComponentStyleSelector(uid, PageID))
  return { style }
}

export const useProjectUIElementsList = (ui) =>
  useSelector(
    (state) =>
      state
        .getIn(['project', 'Settings', 'elements'], Map())
        .filter((elem) => elem.get('parent') === ui)
        .map((_, k) => k)
        .toList(),
    isSame
  )

export const transformCSSPositionValue = (value, units, isEmpty = false) => {
  if (value === 'auto') {
    return 'auto'
  } else if (value === 'min-content') {
    return 'auto'
  } else if (value === 'max-content') {
    return 'max-content'
  } else if (units === 'calc') {
    return `calc(${value})`
  } else if (value === 'unset') {
    return value
  } else if (value) {
    return `${value}${units}`
  } else if (isEmpty) {
    return ''
  } else {
    return 'unset'
  }
}

export const getElementPosition = (component, mode, pageID, devPage, pageUID) => {
  const left = getValueForMode(component, mode, [], ['position', 'left'], '')
  const rotate = getValueForMode(component, mode, [], ['position', '--rotate'], '')
  const leftUnit = getValueForMode(component, mode, [], ['position', 'leftUnit'], 'px')
  const top = getValueForMode(component, mode, [], ['position', 'top'], '')
  const topUnit = getValueForMode(component, mode, [], ['position', 'topUnit'], 'px')
  const right = getValueForMode(component, mode, [], ['position', 'right'], '')
  const rightUnit = getValueForMode(component, mode, [], ['position', 'rightUnit'], 'px')
  const bottom = getValueForMode(component, mode, [], ['position', 'bottom'], '')
  let zIndex = getValueForMode(component, mode, [], ['position', 'zIndex'], '')
  const transform = getValueForMode(component, mode, [], ['position', 'transform'], '')
  const bottomUnit = getValueForMode(component, mode, [], ['position', 'bottomUnit'], 'px')
  const floatType = getValueForMode(component, mode, [], ['position', 'floatType'], '')
  const floatXOffset = getValueForMode(component, mode, [], ['position', 'floatXOffset'], '8')
  const floatXOffsetUnit = getValueForMode(component, mode, [], ['position', 'floatXOffsetUnit'], 'px')
  const floatYOffset = getValueForMode(component, mode, [], ['position', 'floatYOffset'], '8')
  const floatYOffsetUnit = getValueForMode(component, mode, [], ['position', 'floatYOffsetUnit'], 'px')
  const floatPosition = getValueForMode(component, mode, [], ['position', 'floatPosition'], 'bottom-right')
  const allowFullWidth = getValueForMode(component, mode, [], ['position', 'allowFullWidth'], false)
  const allowFullPage = getValueForMode(component, mode, [], ['position', 'allowFullPage'], false)
  const inlinePos = component.getIn([mode, 'position', '--inline-pos'], false)
  const usePageID = String(devPage && devPage !== 'preview' ? devPage : pageUID)
    .replace('signin', 'login')
    .replace('verify', 'login')
  const hide =
    component.getIn([mode, 'isHidden'], false) ||
    component.getIn([mode, 'hideOnPage', usePageID], component.getIn([mode, 'hideOnPage', pageID], false))

  const sizes = component.getIn([mode, 'position', 'disableInherit'])
    ? _.reduce(
        ALL_SIZE_KEYS_NO_UNIT,
        (r, key) => {
          let val = component.getIn([mode, 'position', key], '')
          let valUnit = component.getIn([mode, 'position', `${key}Unit`], 'px')
          if (key == 'width' || key == 'height') {
            if (!val) {
              return {
                ...r,
              }
            }
          }
          if (val === '') {
            // val = 'unset'
            // valUnit = ''
            return r
          } else {
            return {
              ...r,
              [key]: transformCSSPositionValue(val, valUnit, true),
            }
          }
        },
        {}
      )
    : {}

  if (floatType == 'page') {
    if (parseInt(zIndex) < 5 || isNaN(parseInt(zIndex))) {
      zIndex = 5
    }
  }

  return {
    left,
    leftUnit,
    top,
    topUnit,
    right,
    rightUnit,
    bottom,
    bottomUnit,
    rotate,
    zIndex,
    hide,
    transform,
    floatType,
    floatXOffset,
    floatXOffsetUnit,
    floatYOffset,
    floatYOffsetUnit,
    floatPosition,
    allowFullWidth,
    allowFullPage,
    inlinePos,
    ...sizes,
  }
}

export const usePageElementPosition = (uid, PageID, isPreview, customMode) => {
  let mode = useLayoutMode()
  const loc = useLocation()
  const isPublish = String(loc.search).indexOf('ftnocache') === -1
  const isNotDeviceMode = String(loc.search).indexOf('device') === -1
  const projectType = useSelector((state) => state.getIn(['project', 'ProjectType'], 'web'))
  const stripRoot = useSelector(getStripRootMeta, shallowEqual)
  if (customMode) {
    mode = customMode
  }
  const isRTL = useLangDirection()
  const pageID = useSelector((state) => state.get('page'))
  const pageUID = useSelector((state) => getPageUID(state.get('page'), state.getIn(['project', 'Pages'], List())))
  const component = useSelector((state) =>
    state.getIn(
      PageID === 'project' ? ['project', 'Settings', 'elements', uid] : ['pages', PageID, 'Props', 'elements', uid],
      Map()
    )
  )
  const isConfiguredAutoFit = useSelector((state) =>
    state.getIn(['pages', PageID, 'Props', 'isConfiguredAutoFit', mode])
  )
  const parentType = useSelector((state) => {
    const path =
      PageID === 'project'
        ? ['project', 'Settings', 'elements', component.get('parent')]
        : ['pages', PageID, 'Props', 'elements', component.get('parent')]
    const type = state.getIn([...path, 'type'])
    if (type === 'Modal') {
      if (state.getIn([...path, 'props', 'isExp'])) {
        return 'Section'
      }
    }
    if (type === 'Widget') {
      return state.getIn([...path, 'props', 'data', 'Type'], type)
    } else {
      return type
    }
  })
  const devPage = useSelector((state) => {
    const dPage = state.get('devPage', null)
    const pPage = state.getIn(['state', 'page'])
    if (dPage) {
      return dPage
    } else if (pPage) {
      return pPage
    } else {
      return null
    }
  })
  const direction = useSelector((state) => {
    const parentComponent = state.getIn(
      PageID === 'project'
        ? ['project', 'Settings', 'elements', component.get('parent')]
        : ['pages', PageID, 'Props', 'elements', component.get('parent')],
      Map()
    )
    return getValueForMode(parentComponent, mode, ['props'], ['direction'], 'row')
  }, isSame)

  const {
    left,
    leftUnit,
    top,
    topUnit,
    right,
    rightUnit,
    bottom,
    bottomUnit,
    rotate,
    zIndex,
    hide,
    transform,
    floatType,
    floatXOffset,
    floatXOffsetUnit,
    floatYOffset,
    floatYOffsetUnit,
    floatPosition,
    allowFullWidth,
    allowFullPage,
    inlinePos,
    ...sizes
  } = getElementPosition(
    component.get('props', Map()),
    mode,
    pageID,
    component.get('parent') === 'Header' ? devPage : undefined,
    pageUID
  )

  let zIndexVal = ''
  if (zIndex !== undefined && zIndex !== '' && zIndex !== null) {
    zIndexVal = `z-index: ${zIndex};`
  }

  const customSize = () => {
    let rotate = component.getIn(['props', mode, 'position', '--rotate'], '')
      ? `;--rotate: ${component.getIn(['props', mode, 'position', '--rotate'], '')}deg;`
      : ''
    if (!DISABLE_INHERIT_LIST.includes(component.get('type'))) {
      let sizeStyles = sizes
      let overflowCss = ''
      if (component.get('type') === 'Typography' && sizeStyles.height !== '' && sizeStyles.height !== 'auto') {
        overflowCss = ';overflow: auto;'
      }
      return _.map(sizeStyles, (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}`).join(';') + rotate + overflowCss
    } else {
      return rotate
    }
  }

  const sizeCss = () => {
    if (!DISABLE_INHERIT_LIST.includes(component.get('type'))) {
      let sizeStyles = sizes
      if (!_.isEmpty(sizeStyles)) {
        let overflowCss = ''
        if (component.get('type') === 'Typography' && sizeStyles.height !== '' && sizeStyles.height !== 'auto') {
          overflowCss = ';overflow: auto;'
        } else if (component.get('type') === 'Modal') {
          return `.ft--${component.getIn(['props', 'styleRef'])}--root{${_.map(sizeStyles, (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}`).join(';')}${overflowCss}}`
        }
        return `.ft--pos--${uid}, .ft--pos--${uid}.ft--${component.getIn(['props', 'styleRef'])}--root, .ft--pos--${uid} > .ft--${component.getIn(['props', 'styleRef'])}--root{${_.map(sizeStyles, (val, key) => `${kebabCase(key)}:${_.trim(val, ';')}`).join(';')}${overflowCss}}`
      } else {
        return ''
      }
    } else {
      return ''
    }
  }

  const transformCSS = () => {
    if (
      transform &&
      !['Stack', 'Form', 'FlexStrip', 'DrawerEl', 'Modal', 'Scroll', 'RepeatAutoFit'].includes(parentType)
    ) {
      if (isRTL) {
        return `transform: ${String(transform).replace('-50%', '50%')};`
      } else {
        return `transform: ${transform};`
      }
    }
    return ''
  }

  const hideCSS = () => {
    if (isPreview) {
      if (!component.get('parent') && component.get('type') === 'PageConditionalForm') {
        return ''
      }
      return hide ? 'display: none !important;' : ''
    } else {
      if (!component.get('parent') && component.get('type') === 'PageConditionalForm') {
        return 'visibility: visible;'
      }
      return hide ? 'opacity: 0.5 !important;' : 'visibility: visible;'
    }
  }

  const getFloatPosition = (floatType, floatXVal, floatYVal, floatPosition, isRTL) => {
    if (floatType !== '') {
      let position = floatType === 'parent' ? 'absolute' : 'fixed'
      let left = 'left'
      let right = 'right'
      let translateX = '-50%'
      const previewMinX = 82 + parseInt(floatYVal, 10)
      switch (floatPosition) {
        case 'top-center':
          if (!isPreview && floatType === 'page') {
            return `position: ${position} !important;transform: translateX(${translateX}) !important;${left}: 50% !important;top: ${previewMinX}px !important;${right}: unset;bottom: unset;`
          }
          return `position: ${position} !important;transform: translateX(${translateX}) !important;${left}: 50% !important;top: 0px !important;${right}: unset;bottom: unset;`
        case 'bottom-center':
          return `position: ${position} !important;transform: translateX(${translateX}) !important;${left}: 50% !important;top: unset !important;${right}: unset;bottom: ${floatYVal};`
        case 'center':
          return `position: ${position} !important;transform: translate(${translateX},-50%) !important;${left}: 50% !important;top: 50% !important;${right}: unset;bottom: unset;`
        case 'top-left':
          if (!isPreview && floatType === 'page') {
            return `position: ${position} !important;transform: none !important;${left}: ${floatXVal} !important;top: ${previewMinX}px !important;${right}: unset;bottom: unset;margin-left: 42px;`
          }
          return `position: ${position} !important;transform: none !important;${left}: ${floatXVal} !important;top: ${floatYVal} !important;${right}: unset;bottom: unset;`
        case 'top-right':
          if (!isPreview && floatType === 'page') {
            return `position: ${position} !important;transform: none !important;${right}: ${floatXVal} !important;top: ${previewMinX}px !important;${left}: unset;bottom: unset;`
          }
          return `position: ${position} !important;transform: none !important;${right}: ${floatXVal} !important;top: ${floatYVal} !important;${left}: unset;bottom: unset;`
        case 'left-middle':
          return `position: ${position} !important;transform: translateY(-50%) !important;${left}: ${floatXVal} !important;top: 50% !important;${right}: unset;bottom: unset;`
        case 'right-middle':
          return `position: ${position} !important;transform: translateY(-50%) !important;${right}: ${floatXVal} !important;top: 50% !important;${left}: unset;bottom: unset;`
        case 'bottom-left':
          return `position: ${position} !important;transform: none !important;${left}: ${floatXVal} !important;bottom: ${floatYVal} !important;${right}: unset;top: unset;`
        case 'bottom-right':
          return `position: ${position} !important;transform: none !important;${right}: ${floatXVal} !important;bottom: ${floatYVal} !important;${left}: unset;top: unset;`
      }
    } else {
      if (isConfiguredAutoFit && _.startsWith(uid, 'fit_')) {
        if (isRTL) {
          return 'margin-left: var(--project-drawer-width);max-width: calc(var(--content-width) - var(--project-drawer-width) - var(--scroll-width, 0));'
        } else {
          return 'max-width: calc(var(--content-width) - var(--project-drawer-width) - var(--scroll-width, 0));'
        }
      }
      return ''
    }
  }

  const getInlinePos = () => {
    if (inlinePos) {
      return `position: initial !important;transform: none !important;`
    } else {
      return ''
    }
  }

  return useMemo(() => {
    let floatXVal = transformCSSPositionValue(floatXOffset || 0, floatXOffsetUnit)
    let floatYVal = transformCSSPositionValue(floatYOffset || 0, floatYOffsetUnit)
    if (
      [
        'top-center',
        'bottom-center',
        'center',
        'top-left',
        'top-right',
        'left-middle',
        'right-middle',
        'bottom-left',
        'bottom-right',
      ].includes(floatPosition)
    ) {
      if (floatXVal === 'unset') {
        floatXVal = '0px'
      }
      if (floatYVal === 'unset') {
        floatYVal = '0px'
      }
    }
    const position = getFloatPosition(floatType, floatXVal, floatYVal, floatPosition, isRTL)
    let allowAutoHeight = ''
    let extraKey = ''
    let extra = ''
    if (
      component.get('type') === 'Typography' &&
      (parentType === 'Stack' || parentType === 'RepeatAutoFit' || parentType === 'Scroll')
    ) {
      if (direction === 'row') {
        allowAutoHeight = 'min-height: auto !important;'
      } else {
        allowAutoHeight = 'min-width: auto !important;'
      }
    }
    if (component.get('type') === 'PageConditionalForm' && !isPreview) {
      return {
        position: `position: absolute;
                    left: unset;
                    top: unset;
                    right: unset;
                    bottom: unset;
                    z-index: ;
                    visibility: visible;
                    left: 377px;
                    transform: unset !important;
                    right: 409px;
                    top: 50px;
                    bottom: 0;
                    position: fixed;
                    width: auto !important;
                    max-width: calc(${stripRoot[mode]})!important;
                    ${mode !== 'lg' ? 'margin: 0 auto;' : ''}
                    height: calc(100vh - 50px) !important;${getInlinePos()}`,
        vis: `${Boolean(zIndex) && ~~zIndex !== '0' ? `z-index: ${zIndex};` : ''} ${hideCSS()} `,
      }
    }

    let fullWidthModify = ''
    if (allowFullWidth || component.get('type') === 'Banner') {
      if (isPreview) {
        if (isNotDeviceMode || mode === 'lg') {
          fullWidthModify = `width: 100vw!important;left: 50%!important;transform: translateX(-50%)!important;right: unset!important;max-width: 100vw !important;`
        } else {
          fullWidthModify = `width: calc(${stripRoot[mode]})!important;max-width: calc(${stripRoot[mode]})!important;left: 0 !important;right: unset!important;`
        }
      } else {
        if (mode === 'lg') {
          fullWidthModify = `width: calc(100vw - 30px)!important;max-width: calc(100vw - 30px)!important;left: 50%;transform: translateX(-50%);right: unset;`
        } else {
          fullWidthModify = `width: calc(${stripRoot[mode]})!important;max-width: calc(${stripRoot[mode]})!important;left: 50%;transform: translateX(-50%);right: unset;`
        }
      }
    }
    if (allowFullPage) {
      extraKey = '.ft--theme--ContinualForm--content'
      if (isPreview) {
        if (isPublish || mode === 'lg') {
          fullWidthModify = `left: 0 !important;right: 0!important; bottom: 0 !important;top: 0 !important;position: fixed !important;height: 100vh !important;width: 100vw !important;`
          extra = 'width: 100vw !important;height: 100vh !important;border: none !important;'
        } else {
          fullWidthModify = `min-width: 100vw; width: calc(${stripRoot[mode]})!important;left: unset !important;right: unset;transform: unset !important;position: static !important;border: none !important;bottom: 0 !important;top: 0 !important;height: 100vh !important;`
          extra = `min-width: 100vw;width: calc(${stripRoot[mode]}) !important;height: calc(100vh) !important;`
          if (component.get('type') === 'PageConditionalForm') {
            fullWidthModify += `max-width: calc(${stripRoot[mode]}) !important;min-width: calc(${stripRoot[mode]}) !important;`
          }
        }
        if (component.get('type') === 'PageConditionalForm') {
          fullWidthModify += `max-height: 100vh;
                        min-height: 100vh;
                        position: fixed !important;
                        left: 0 !important;
                        top: 0 !important;
                        bottom: 0 !important;
                        right: 0 !important;
                        margin: 0 auto;`
        }
      } else {
        if (mode === 'lg') {
          fullWidthModify = `left: 30px;transform: unset !important;right: 0; top: 50px; bottom: 0;position: fixed;width: auto !important;height: calc(100vh - 50px) !important;`
          extra = 'width: calc(100vw - 30px) !important;height: calc(100vh - 50px) !important;'
        } else {
          fullWidthModify = `width: calc(${stripRoot[mode]})!important;left: calc(50% + 16px);transform: translateX(-50%);right: 0; top: 50px; bottom: 0;position: fixed;height: calc(100vh - 50px) !important;`
          extra = `width: calc(${stripRoot[mode]}) !important;height: calc(100vh - 50px) !important;`
        }
      }
    }

    if (isRTL) {
      if (projectType === LANDING_TYPE) {
        return {
          position: `position: relative;${transformCSS()}${hideCSS()}${position}${allowAutoHeight}${fullWidthModify}${customSize()}${getInlinePos()}`,
          vis: `${Boolean(zIndex) && ~~zIndex !== '0' ? `z-index: ${zIndex}` : ''} ${hideCSS()} `,
          size: sizeCss(),
        }
      }
      switch (parentType) {
        case 'Modal':
        case 'DrawerEl':
        case 'PageConditionalForm':
        case 'Form': {
          let stl = ''
          if (component.get('type') === 'Typography' && parentType === 'PageConditionalForm') {
            stl = 'width: auto !important;'
          }
          return {
            position: `position: relative;left: unset;right: unset;top: unset;bottom: unset;
                            ${zIndexVal}
                            ${transformCSS()}
                            ${hideCSS()}${position}${fullWidthModify}${stl}${customSize()}${getInlinePos()}`,
            vis: `${Boolean(zIndex) && ~~zIndex !== '0' ? `z-index: ${zIndex};` : ''} ${hideCSS()} `,
            extraKey,
            extra,
          }
        }
        case 'Scroll':
          return {
            position: `position: relative;${transformCSS()}${hideCSS()}${position}left: unset;right: unset;top: unset;bottom: unset;${allowAutoHeight}${fullWidthModify}${customSize()}${getInlinePos()}`,
            vis: `${Boolean(zIndex) && ~~zIndex !== '0' ? `z-index: ${zIndex};` : ''} ${hideCSS()} `,
          }
        case 'RepeatAutoFit':
        case 'Stack':
        case 'Scheduler':
          return {
            position: `position: initial;${transformCSS()}${hideCSS()}${position}${allowAutoHeight}${fullWidthModify}${customSize()}${getInlinePos()}`,
            vis: `${Boolean(zIndex) && ~~zIndex !== '0' ? `z-index: ${zIndex};` : ''} ${hideCSS()} `,
          }
        default:
          if (projectType === CHAT_BOT_TYPE) {
            return {
              position: `position: initial;${transformCSS()}${hideCSS()}${position}${allowAutoHeight}${fullWidthModify}${customSize()}${getInlinePos()}`,
              vis: `${Boolean(zIndex) && ~~zIndex !== '0' ? `z-index: ${zIndex};` : ''} ${hideCSS()} `,
              size: sizeCss(),
            }
          }
          return {
            position: `position: absolute;right: ${transformCSSPositionValue(left, leftUnit)};top: ${transformCSSPositionValue(top, topUnit)};left: ${transformCSSPositionValue(right, rightUnit)};bottom: ${transformCSSPositionValue(bottom, bottomUnit)};
                            ${zIndexVal}
                        ${transformCSS()}
                        ${position}
                        ${hideCSS()}${fullWidthModify}${customSize()}${getInlinePos()}`,
            vis: `${Boolean(zIndex) && ~~zIndex !== '0' ? `z-index: ${zIndex};` : ''} ${hideCSS()} `,
            ...(component.get('type') === 'CarouselContainer' && allowFullWidth
              ? {
                  extraKey: '.ft--theme--CarouselContainer--content',
                  extra: `width: 100vw!important`,
                }
              : {
                  extraKey,
                  extra,
                }),
            size: sizeCss(),
          }
      }
    } else {
      if (projectType === LANDING_TYPE) {
        return {
          position: `position: relative;${transformCSS()}${hideCSS()}${position}${allowAutoHeight}${fullWidthModify}${customSize()}${getInlinePos()}`,
          vis: `${Boolean(zIndex) && ~~zIndex !== '0' ? `z-index: ${zIndex}` : ''} ${hideCSS()} `,
          size: sizeCss(),
        }
      }
      switch (parentType) {
        case 'Scroll':
          return {
            position: `position: relative;${transformCSS()}${hideCSS()}${position}left: unset;right: unset;top: unset;bottom: unset;${allowAutoHeight}${fullWidthModify}${customSize()}${getInlinePos()}`,
            vis: `${Boolean(zIndex) && ~~zIndex !== '0' ? `z-index: ${zIndex};` : ''} ${hideCSS()} `,
          }
        case 'RepeatAutoFit':
        case 'Stack':
        case 'Scheduler':
          return {
            position: `position: initial;${transformCSS()}${hideCSS()}${position}${allowAutoHeight}${fullWidthModify}${customSize()}${getInlinePos()}`,
            vis: `${Boolean(zIndex) && ~~zIndex !== '0' ? `z-index: ${zIndex};` : ''} ${hideCSS()} `,
          }
        case 'Modal':
        case 'DrawerEl':
        case 'PageConditionalForm':
        case 'Form': {
          let stl = ''
          if (component.get('type') === 'Typography' && parentType === 'PageConditionalForm') {
            stl = 'width: auto !important;'
          }
          return {
            position: `position: relative;
                            left: unset;
                            right: unset;
                            top: unset;
                            bottom: unset;
                            ${zIndexVal}
                        ${transformCSS()}
                        ${position}
                        ${hideCSS()}${fullWidthModify}${stl}${customSize()}${getInlinePos()}`,
            vis: `${Boolean(zIndex) && ~~zIndex !== '0' ? `z-index: ${zIndex};` : ''} ${hideCSS()} `,
            extraKey,
            extra,
            size: sizeCss(),
          }
        }
        default:
          if (projectType === CHAT_BOT_TYPE) {
            return {
              position: `position: initial;${transformCSS()}${hideCSS()}${position}${allowAutoHeight}${fullWidthModify}${customSize()}${getInlinePos()}`,
              vis: `${Boolean(zIndex) && ~~zIndex !== '0' ? `z-index: ${zIndex};` : ''} ${hideCSS()} `,
              size: sizeCss(),
            }
          }
          return {
            position: `position: absolute;
                            left: ${transformCSSPositionValue(left, leftUnit)};
                            top: ${transformCSSPositionValue(top, topUnit)};
                            right: ${transformCSSPositionValue(right, rightUnit)};
                            bottom: ${transformCSSPositionValue(bottom, bottomUnit)};
                            ${zIndexVal}
                    ${transformCSS()}
                    ${position}
                    ${hideCSS()}${fullWidthModify}${customSize()}${getInlinePos()}`,
            vis: `${Boolean(zIndex) && ~~zIndex !== '0' ? `z-index: ${zIndex};` : ''} ${hideCSS()} `,
            ...(component.get('type') === 'CarouselContainer' && allowFullWidth
              ? {
                  extraKey: '.ft--theme--CarouselContainer--content',
                  extra: `width: 100vw!important`,
                }
              : {
                  extraKey,
                  extra,
                }),
            size: sizeCss(),
          }
      }
    }
  }, [
    left,
    leftUnit,
    top,
    topUnit,
    right,
    rightUnit,
    bottom,
    bottomUnit,
    isRTL,
    zIndex,
    hide,
    transform,
    parentType,
    floatType,
    floatXOffset,
    floatXOffsetUnit,
    floatYOffset,
    floatYOffsetUnit,
    floatPosition,
    direction,
    allowFullWidth,
    mode,
    isPublish,
    allowFullPage,
    projectType,
    component,
    rotate,
    inlinePos,
  ])
}

export const useProjectTheme = () => {
  const theme = useSelector((state) => state.getIn(['project', 'Settings', 'theme'], Map()))
  return { theme }
}

export const getComponentStyles = (state, uid, PageID) => {
  const styleRef =
    PageID === 'project'
      ? state.getIn(['project', 'Settings', 'elements', uid, 'props', 'styleRef'])
      : state.getIn(['pages', PageID, 'Props', 'elements', uid, 'props', 'styleRef'])
  const isGlobalStyle = state.getIn(['project', 'Settings', 'styles', styleRef, 'isGlobal'])
  if (isGlobalStyle) {
    return state.getIn(['project', 'Settings', 'styles', styleRef], Map())
  } else {
    return state.getIn(
      PageID === 'project'
        ? ['project', 'Settings', 'styles', styleRef]
        : ['pages', PageID, 'Props', 'styles', styleRef],
      Map()
    )
  }
}

export const useComponentStyle = (uid, PageID, componentStyleRef) => {
  const componentStyles = useSelector((state) => {
    const styleRef = componentStyleRef
      ? componentStyleRef
      : PageID === 'project'
        ? state.getIn(['project', 'Settings', 'elements', uid, 'props', 'styleRef'])
        : state.getIn(['pages', PageID, 'Props', 'elements', uid, 'props', 'styleRef'])
    const isGlobalStyle = state.getIn(['project', 'Settings', 'styles', styleRef, 'isGlobal'])
    if (isGlobalStyle) {
      return state.getIn(['project', 'Settings', 'styles', styleRef], Map())
    } else {
      return state.getIn(
        PageID === 'project'
          ? ['project', 'Settings', 'styles', styleRef]
          : ['pages', PageID, 'Props', 'styles', styleRef],
        Map()
      )
    }
  }, shallowEqual)

  return {
    componentStyles,
  }
}

export function useSelectedSize() {
  const selectedSize = useSelector((state) => state.getIn(['selected'], List()).size)
  return { selectedSize }
}

export function useIsSelected(uid) {
  const isSelected = useSelector((state) => state.getIn(['selected'], List()).indexOf(uid) !== -1)
  return { isSelected }
}

export function useIsPreloadingElement(uid, isPreview, defaultValue, type) {
  if (!isPreview) {
    return false
  }

  let isEffectedByFormula = useSelector((state) => state.getIn(['project', 'isEffectedFormulaFields', uid], false))
  let isLoading = useSelector((state) => state.getIn(['isLoadingElements', uid], defaultValue))
  if (type == 'FormField') {
    isLoading = false
  }

  if (IS_SERVER && isEffectedByFormula) {
    isLoading = true
  }

  return isLoading
}

export function useIsGridSelected(uid, type) {
  const isSelected = useSelector(
    (state) =>
      state.getIn(['selected'], List()).indexOf(uid) !== -1 &&
      (type === 'Form' || type === 'PowerTable' ? true : state.getIn(['selectedArea'], List()).size === 0)
  )
  const selected = useSelector((state) => state.get('selected', List()))

  // const isLastSelected = useSelector(state => state.getIn(['selected'], List()).indexOf(uid) === state.getIn(['selected'], List()).size - 1)

  const isShowToolbar = useMemo(() => {
    let isShowToolbar = false
    const selectedJS = selected.toJS()
    if (selectedJS.indexOf(uid) !== -1) {
      if (selectedJS.length === 1) {
        isShowToolbar = true
      }
      const bottoms = _.map(selectedJS, (uid) => {
        const el = document.getElementById(uid)
        if (el) {
          return {
            uid,
            bottom: el.getBoundingClientRect().bottom,
          }
        } else {
          return {
            uid,
            bottom: -1,
          }
        }
      })
      const last = _.last(_.uniqBy(_.sortBy(bottoms, 'bottom'), 'bottom')).uid === uid
      isShowToolbar = last
    } else {
      isShowToolbar = false
    }
    return isShowToolbar
  }, [selected, isSelected])

  return { isSelected, isShowToolbar }
}

export function useIsStripColSelected(uid, col) {
  const isSelectedCol = useSelector(
    (state) =>
      state.getIn(['selected'], List()).indexOf(uid) !== -1 &&
      (uid === 'Footer' && ['lg', 'md'].indexOf(state.get('layoutMode', 'lg')) === -1
        ? true
        : state.getIn(['selectedArea'], List()).indexOf(col) !== -1)
  )
  return { isSelectedCol }
}

export function useIsColSelected() {
  const isSelectedCol = useSelector((state) => state.getIn(['selectedArea'], List()).size > 0)
  return { isSelectedCol }
}
